import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isOrgPortal } from "./lib/orgPortal";
import { RedirectGate } from "./pages/PlatformRedirects";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";import { PhoneProvider } from "./context/PhoneContext";import { ConsumerAuthProvider, useConsumer } from "./context/ConsumerAuthContext";
import { ConsumerBillingProvider } from "./context/ConsumerBillingContext";
import Layout from "./components/Layout";
import { MfaRequiredGate } from "./components/MfaRequiredGate";
import Login from "./pages/Login";
import { applyAppIcon, firmIcon } from "./lib/appIcon";
import NoAccess from "./components/NoAccess";
import ComingSoon from "./components/ComingSoon";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const MatterHome = lazy(() => import("./pages/MatterHome"));
const MatterAppointments = lazy(() => import("./pages/MatterAppointments"));
const PackageServices = lazy(() => import("./pages/PackageServices"));
const ClaimsMap = lazy(() => import("./pages/ClaimsMap"));
const ClaimDetail = lazy(() => import("./pages/ClaimDetail"));
const Documents = lazy(() => import("./pages/Documents"));
const Activity = lazy(() => import("./pages/Activity"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Research = lazy(() => import("./pages/Research"));
const Focused = lazy(() => import("./pages/Focused"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Firm = lazy(() => import("./pages/Firm"));
const FirmMessages = lazy(() => import("./pages/Firm").then((m) => ({ default: m.FirmMessages })));
const FirmIntake = lazy(() => import("./pages/FirmIntake"));
const IntakeDetail = lazy(() => import("./pages/IntakeDetail"));
const Conflicts = lazy(() => import("./pages/Conflicts"));
const ConflictDetail = lazy(() => import("./pages/ConflictDetail"));
const FirmIdentities = lazy(() => import("./pages/FirmIdentities"));
const FirmPayments = lazy(() => import("./pages/FirmPayments"));
const FirmIdentityDetail = lazy(() => import("./pages/FirmIdentityDetail"));
const FirmPhoneApi = lazy(() => import("./pages/FirmPhoneApi"));
const FirmPhoneCalls = lazy(() => import("./pages/FirmPhoneCalls"));
const PhoneDialer = lazy(() => import("./pages/PhoneDialer"));
const DocumentTypes = lazy(() => import("./pages/DocumentTypes"));
const AIResearch = lazy(() => import("./pages/AIResearch"));
const PlatformIdentityDetail = lazy(() => import("./pages/PlatformIdentityDetail"));
const CallRoom = lazy(() => import("./pages/CallRoom"));
const FirmScheduling = lazy(() => import("./pages/FirmScheduling"));
const FirmAITraining = lazy(() => import("./pages/FirmAITraining"));
const Summarizer = lazy(() => import("./pages/Summarizer"));
const Emails = lazy(() => import("./pages/Emails"));
import IdleLogout from "./components/IdleLogout";
const MatterEmails = lazy(() => import("./pages/MatterEmails"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const Platform = lazy(() => import("./pages/Platform"));
const PlatformLayout = lazy(() => import("./components/PlatformLayout"));
const PlatformProfile = lazy(() => import("./pages/PlatformProfile"));
const FirmUserDetail = lazy(() => import("./pages/FirmUserDetail"));
const TenantDetail = lazy(() => import("./pages/TenantDetail"));
const ConsumerDetail = lazy(() => import("./pages/ConsumerDetail"));
const ProDetail = lazy(() => import("./pages/ProDetail"));
const ProcessManager = lazy(() => import("./pages/ProcessManager"));
const FormBuilderPage = lazy(() => import("./pages/FormBuilderPage"));
const AgreementBuilderPage = lazy(() => import("./pages/AgreementBuilderPage"));
const FirmEvidenceReview = lazy(() => import("./pages/FirmEvidenceReview"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const ClientLogin = lazy(() => import("./pages/client/ClientLogin"));
const ClientPortal = lazy(() => import("./pages/client/ClientPortal"));
const FirmLogin = lazy(() => import("./pages/client/FirmLogin"));
const PublicForm = lazy(() => import("./pages/PublicForm"));
const PublicInvoicePay = lazy(() => import("./pages/PublicInvoicePay"));
const ConsumerLogin = lazy(() => import("./pages/consumer/ConsumerLogin"));
const OrgShell = lazy(() => import("./components/OrgShell"));
const ConsumerShell = lazy(() => import("./components/ConsumerShell"));
const OrgP = (n) => lazy(() => import("./pages/organization/OrgPages").then((m) => ({ default: m[n] })));
const OrgHome = OrgP("OrgHome"), OrgLibrary = OrgP("OrgLibrary"), OrgPlaceholder = OrgP("OrgPlaceholder"), OrgBilling = OrgP("OrgBilling"),
  OrgSupport = OrgP("OrgSupport"), OrgAdmin = OrgP("OrgAdmin"), OrgCreate = OrgP("OrgCreate");
const OrgApiLinkage = lazy(() => import("./pages/organization/OrgApiLinkage"));
const OrgMediationSessions = lazy(() => import("./pages/organization/OrgMediations").then((m) => ({ default: m.OrgMediationSessions })));
const MediationStep1 = lazy(() => import("./pages/consumer/MediationStep1"));
// Warm the consumer login chunk immediately so logged-out root visitors land on /login
// without a second loading flash.
import("./pages/consumer/ConsumerLogin");
const LegalPage = lazy(() => import("./pages/LegalPage"));
const LinkSupport = lazy(() => import("./pages/LinkSupport"));
const ConsumerHub = lazy(() => import("./pages/consumer/ConsumerHub"));
const HireCounsel = lazy(() => import("./pages/consumer/HireCounsel"));
const ConsumerReset = lazy(() => import("./pages/consumer/ConsumerReset"));
const PublicFirmSite = lazy(() => import("./pages/PublicFirmSite"));
const SlugRedirect = lazy(() => import("./components/SlugRedirect"));
const MktAgreements = lazy(() => import("./pages/marketing/Agreements"));
const MktEstate = lazy(() => import("./pages/marketing/EstateDocuments"));
const MktHome = lazy(() => import("./pages/marketing/Home"));
const LawfirmMarketing = lazy(() => import("./pages/lawfirm-marketing/LawfirmMarketing"));
const MktMediate = lazy(() => import("./pages/marketing/Mediate"));
const MktOrganization = lazy(() => import("./pages/marketing/Organization"));
const MktArbitrate = lazy(() => import("./pages/marketing/Arbitrate"));



function ScrollToTop() {
  const { pathname } = useLocation();
  React.useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const reset = () => {
      window.scrollTo(0, 0);
      // `body` is the ACTUAL scroll container here (its overflow-y computes to
      // `auto` because overflow-x is hidden + height:100%), so window.scrollTo
      // alone doesn't move it — reset the element scrollers directly too.
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };
    reset();
    const r = requestAnimationFrame(reset);
    const t = setTimeout(reset, 80);
    return () => { cancelAnimationFrame(r); clearTimeout(t); };
  }, [pathname]);
  return null;
}
const ConsultBooking = lazy(() => import("./pages/ConsultBooking"));
const ClientCall = lazy(() => import("./pages/ClientCall"));
const ConsumerPlans = lazy(() => import("./pages/consumer/ConsumerPlans"));
const CreateContract = lazy(() => import("./pages/consumer/CreateContract"));
const MyAgreements = lazy(() => import("./pages/consumer/MyAgreements"));
const MediationCase = lazy(() => import("./pages/consumer/MediationCases"));
const MediationCaseList = lazy(() => import("./pages/consumer/MediationCases").then((m) => ({ default: m.MediationCaseList })));
const MediationEntry = lazy(() => import("./pages/consumer/MediationCases").then((m) => ({ default: m.MediationEntry })));
const ArbitrationEntry = lazy(() => import("./pages/consumer/Arbitration").then((m) => ({ default: m.ArbitrationEntry })));
const ArbitrationStart = lazy(() => import("./pages/consumer/Arbitration").then((m) => ({ default: m.ArbitrationStart })));
const ArbitrationCases = lazy(() => import("./pages/consumer/Arbitration").then((m) => ({ default: m.ArbitrationCases })));
const SignAgreement = lazy(() => import("./pages/consumer/SignAgreement"));
const NegotiateAgreement = lazy(() => import("./pages/consumer/NegotiateAgreement"));
const ConsumerSettings = lazy(() => import("./pages/consumer/ConsumerSettings"));
const ConsumerBilling = lazy(() => import("./pages/consumer/ConsumerBilling"));
const EstateHub = lazy(() => import("./pages/estate/EstateHub"));
const ConsumerEstate = lazy(() => import("./pages/consumer/ConsumerEstate"));
const ConsumerEstateVerify = lazy(() => import("./pages/consumer/ConsumerEstateVerify"));
const EstateDraft = lazy(() => import("./pages/estate/EstateDraft"));
const MyEstate = lazy(() => import("./pages/estate/MyEstate"));
const EstateJoin = lazy(() => import("./pages/estate/EstateJoin"));
const EstateRoom = lazy(() => import("./pages/estate/EstateRoom"));
const EstateThread = lazy(() => import("./pages/estate/EstateThread"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const VerifyPro = lazy(() => import("./pages/VerifyPro"));
const ProfilePage = lazy(() => import("./pages/consumer/ProfilePage"));
const Inbox = lazy(() => import("./pages/consumer/Inbox"));
import { startTracker } from "./lib/tracker";
import IpGuard from "./components/IpGuard";
import { UploadProvider } from "./context/UploadContext";

function firmLoginTarget() {
  // Prefer the firm's own branded login (remembered from a prior sign-in) so that a
  // home-screen shortcut that reopens a logged-out firm page lands on THAT firm's login.
  try {
    const slug = localStorage.getItem("lx_firm_slug");
    const role = localStorage.getItem("lx_firm_role");
    if (slug) return role === "client" ? `/${slug}/portal` : `/${slug}/admin`;
  } catch (e) { /* noop */ }
  return "/x1";
}

function Protected({ children, client = false }) {
  const { user } = useAuth();
  useEffect(() => { if (user) return startTracker("firm"); }, [user]);
  if (user === null)
    return <div className="h-screen flex items-center justify-center text-muted-foreground font-mono-legal text-sm">Loading…</div>;
  if (!user) return <Navigate to={firmLoginTarget()} replace />;
  if (user.platform_role === "owner") return <Navigate to="/platform" replace />;
  if (user.tenant_role === "client" && !client) return <Navigate to="/client" replace />;
  return <><MfaRequiredGate />{children}</>;
}

function RootProtected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  useEffect(() => { if (user) return startTracker("firm"); }, [user]);
  // Logged-out visitors to the site root belong on the consumer login. Decide this
  // synchronously (before the async /auth/me check resolves) so NO firm chrome or firm
  // loader ever flashes at "/".
  let hasFirmToken = false;
  try { hasFirmToken = !!localStorage.getItem("lm_token"); } catch (e) { /* noop */ }
  if (loc.pathname === "/" && !hasFirmToken) return <MktHome />;
  if (user === null)
    return <div className="h-screen flex items-center justify-center text-muted-foreground font-mono-legal text-sm">Loading…</div>;
  if (!user) return loc.pathname === "/" ? <MktHome /> : <Navigate to={firmLoginTarget()} replace />;
  if (user.platform_role === "owner") return <Navigate to="/platform" replace />;
  if (user.tenant_role === "client") return <Navigate to="/client" replace />;
  return <><MfaRequiredGate />{children}</>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user || user.tenant_role !== "admin") return <NoAccess />;
  return children;
}

function PlatformProtected({ children }) {
  const { user, logout } = useAuth();
  if (user === null)
    return <div className="h-screen flex items-center justify-center text-muted-foreground font-mono-legal text-sm">Loading…</div>;
  if (!user) return <Navigate to="/x1" replace />;
  if (user.platform_role !== "owner") return <Navigate to="/" replace />;
  return <>{children}<IdleLogout minutes={15} onLogout={logout} /></>;
}

// Consumer pages that show the left sidebar (immersive editors/rooms keep their own full-screen layout).
const SHELL_PATHS = [/^\/app\/?$/, /^\/app\/arbitration(\/(new|cases))?\/?$/, /^\/app\/contracts\/new\/?$/, /^\/app\/(inbox|agreements|arbitration|counsel|billing|profile|settings|plans|u)\/?$/, /^\/app\/estate(\/mine)?\/?$/, /^\/app\/mediation(\/.*)?$/];

function ConsumerProtected({ children }) {
  const { user, logout } = useConsumer();
  const loc = useLocation();
  useEffect(() => { if (user) return startTracker("consumer"); }, [user]);
  if (user === null)
    return <div className="h-screen flex items-center justify-center bg-[#05070d] text-white/60 text-sm">Loading…</div>;
  if (!user) {
    const org = loc.pathname.startsWith("/organizations");
    return <Navigate to={`${org ? "/organizations/login" : "/login"}?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }
  // Family Vault members accessing through the embed are NEVER auto-logged out.
  const isEmbed = (() => { try { return localStorage.getItem("lx_embed") === "1"; } catch (e) { return false; } })();
  const inOrg = isOrgPortal() && loc.pathname.startsWith("/app");
  if (inOrg && loc.pathname === "/app") return <Navigate to="/organizations/home" replace />;
  const shelled = !inOrg && !isEmbed && SHELL_PATHS.some((re) => re.test(loc.pathname));
  const body = inOrg ? <OrgShell>{children}</OrgShell> : shelled ? <ConsumerShell>{children}</ConsumerShell> : children;
  return <>{body}<IdleLogout minutes={30} onLogout={logout} enabled={!isEmbed} accent="#8b5cf6" /></>;
}

function OrgRoute({ children }) {
  return <ConsumerProtected><OrgShell>{children}</OrgShell></ConsumerProtected>;
}

function OrgIndex() {
  const { user } = useConsumer();
  if (user === null) return null;
  return <Navigate to={user ? "/organizations/home" : "/organization"} replace />;
}

function LegacyOrgRedirect() {
  const loc = useLocation();
  return <Navigate to={loc.pathname.replace(/^\/organization(?=\/|$)/, "/organizations") + loc.search} replace />;
}

function LegacyConsumerLoginRedirect() {
  const loc = useLocation();
  return <Navigate to={`/login${loc.search}`} replace />;
}

function ConsumerThemeManager() {
  const loc = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    const apply = () => {
      const light = (localStorage.getItem("lx_theme") || "dark") === "light";
      const onConsumer = loc.pathname.startsWith("/app");
      document.documentElement.classList.toggle("lx-light", light && onConsumer);
      const p = loc.pathname;
      // Authenticated firm member / client -> brand EVERY page with their firm
      // (covers standalone firm pages like /focused that aren't inside the sidebar Layout).
      if (user && user.tenant_role && user.platform_role !== "owner" && !p.startsWith("/legal")) {
        const fn = user.display_name || user.tenant_name;
        // iOS/Android PWAs get an isolated (logged-out) session, so deep authenticated
        // pages redirect to the generic Lexarra login. Point the installed app's start_url
        // at the firm's own branded login so reopening lands on THEIR page, not Lexarra.
        const entry = user.slug ? (user.tenant_role === "client" ? `/${user.slug}/portal` : `/${user.slug}/admin`) : undefined;
        if (user.tenant_role === "client") applyAppIcon(firmIcon(user.logo_url, user.slug), fn ? `${fn} - Client Portal` : "Client Portal", entry);
        else applyAppIcon(firmIcon(user.logo_url, user.slug), fn || "Lexarra", entry);
        return;
      }
      const legalFirm = p.startsWith("/legal") && (loc.search.includes("ctx=firm") || loc.search.includes("slug="));
      const generic = p.startsWith("/app") || p.startsWith("/platform") || p.startsWith("/login") || p === "/x1" || p.startsWith("/lawfirm") || (p.startsWith("/legal") && !legalFirm) || p.startsWith("/verify") || p.startsWith("/estate") || p.startsWith("/pay") || p.startsWith("/payment") || p.startsWith("/accept-invite");
      if (generic) applyAppIcon(null, p.startsWith("/verify") ? "Lexarra Verify" : "Lexarra");
    };
    apply();
    window.addEventListener("lx-theme", apply);
    return () => window.removeEventListener("lx-theme", apply);
  }, [loc.pathname, loc.search, user]);
  return null;
}

function FirmPhone({ children }) {
  const { user } = useAuth();
  return <PhoneProvider enabled={!!user && user.slug === "demo-firm" && ["admin", "attorney"].includes(user.tenant_role)}>{children}</PhoneProvider>;
}

function App() {
  return (
    <AuthProvider>
      <ConsumerAuthProvider>
      <ConsumerBillingProvider>
      <Toaster theme="dark" position="top-right" richColors closeButton />
      <BrowserRouter>
        <ConsumerThemeManager />
        <ScrollToTop />
        <IpGuard />
        <UploadProvider>
        <FirmPhone>
        <Suspense fallback={<div className="h-screen w-screen flex items-center justify-center bg-[#05070d] text-white/50 text-sm">Loading…</div>}>
        <Routes>
          <Route path="/app/login" element={<LegacyConsumerLoginRedirect />} />
          <Route path="/app/reset" element={<ConsumerReset />} />
          <Route path="/app/plans" element={<ConsumerProtected><ConsumerPlans /></ConsumerProtected>} />
          <Route path="/app/billing" element={<ConsumerProtected><ConsumerBilling /></ConsumerProtected>} />
          <Route path="/app" element={<ConsumerProtected><ConsumerHub /></ConsumerProtected>} />
          <Route path="/app/estate" element={<ConsumerProtected><ConsumerEstate /></ConsumerProtected>} />
          <Route path="/app/counsel" element={<ConsumerProtected><HireCounsel /></ConsumerProtected>} />
          <Route path="/app/profile" element={<ConsumerProtected><ProfilePage /></ConsumerProtected>} />
          <Route path="/app/u" element={<ConsumerProtected><ProfilePage /></ConsumerProtected>} />
          <Route path="/app/inbox" element={<ConsumerProtected><Inbox /></ConsumerProtected>} />
          <Route path="/app/settings" element={<ConsumerProtected><ConsumerSettings /></ConsumerProtected>} />
          <Route path="/app/mediation" element={<ConsumerProtected><MediationEntry /></ConsumerProtected>} />
          <Route path="/app/mediation/cases" element={<ConsumerProtected><MediationCaseList /></ConsumerProtected>} />
          <Route path="/app/mediation/new" element={<ConsumerProtected><MediationStep1 /></ConsumerProtected>} />
          <Route path="/app/mediation/:id" element={<ConsumerProtected><MediationCase /></ConsumerProtected>} />
          <Route path="/app/arbitration" element={<ConsumerProtected><ArbitrationEntry /></ConsumerProtected>} />
          <Route path="/app/arbitration/new" element={<ConsumerProtected><ArbitrationStart /></ConsumerProtected>} />
          <Route path="/app/arbitration/cases" element={<ConsumerProtected><ArbitrationCases /></ConsumerProtected>} />
          <Route path="/app/agreements" element={<ConsumerProtected><MyAgreements /></ConsumerProtected>} />
          <Route path="/app/sign/:token" element={<ConsumerProtected><SignAgreement /></ConsumerProtected>} />
          <Route path="/app/negotiate/:cid" element={<ConsumerProtected><NegotiateAgreement /></ConsumerProtected>} />
          <Route path="/app/contracts/new" element={<ConsumerProtected><CreateContract /></ConsumerProtected>} />
          <Route path="/app/contracts/:id" element={<ConsumerProtected><CreateContract /></ConsumerProtected>} />
          <Route path="/login" element={<ConsumerLogin key="consumer-auth" />} />
          <Route path="/organizations/login" element={<ConsumerLogin key="org-auth" org />} />
          <Route path="/organizations/create" element={<OrgCreate />} />
          <Route path="/organizations" element={<RedirectGate page="organizations"><OrgIndex /></RedirectGate>} />
          <Route path="/organizations/home" element={<OrgRoute><OrgHome /></OrgRoute>} />
          <Route path="/organizations/messages" element={<OrgRoute><Inbox /></OrgRoute>} />
          <Route path="/organizations/agreements" element={<OrgRoute><MyAgreements /></OrgRoute>} />
          <Route path="/organizations/library" element={<OrgRoute><OrgLibrary /></OrgRoute>} />
          <Route path="/organizations/mediations" element={<OrgRoute><OrgPlaceholder kind="mediations" /></OrgRoute>} />
          <Route path="/organizations/mediations/sessions" element={<OrgRoute><OrgMediationSessions /></OrgRoute>} />
          <Route path="/organizations/arbitrations" element={<OrgRoute><OrgPlaceholder kind="arbitrations" /></OrgRoute>} />
          <Route path="/organizations/counsel" element={<OrgRoute><HireCounsel /></OrgRoute>} />
          <Route path="/organizations/billing" element={<OrgRoute><OrgBilling /></OrgRoute>} />
          <Route path="/organizations/support" element={<OrgRoute><OrgSupport /></OrgRoute>} />
          <Route path="/organizations/profile" element={<OrgRoute><ProfilePage /></OrgRoute>} />
          <Route path="/organizations/identity" element={<OrgRoute><ConsumerSettings /></OrgRoute>} />
          <Route path="/organizations/admin" element={<OrgRoute><OrgAdmin /></OrgRoute>} />
          <Route path="/organizations/api-linkage" element={<OrgRoute><OrgApiLinkage /></OrgRoute>} />
          <Route path="/organization" element={<RedirectGate page="organizations"><MktOrganization /></RedirectGate>} />
          <Route path="/organization/*" element={<LegacyOrgRedirect />} />
          <Route path="/create" element={<ConsumerLogin key="consumer-auth" initialMode="signup" />} />
          <Route path="/agreements" element={<MktAgreements />} />
          <Route path="/agreement" element={<MktAgreements />} />
          <Route path="/estatedocuments" element={<MktEstate />} />
          <Route path="/mediate" element={<MktMediate />} />
          <Route path="/arbitrate" element={<MktArbitrate />} />
          <Route path="/verify" element={<VerifyCertificate />} />
          <Route path="/verify/pro" element={<VerifyPro />} />
          <Route path="/pay/:token" element={<PublicInvoicePay />} />
          <Route path="/x1" element={<Login portal="platform" />} />
          <Route path="/lawfirm" element={<RedirectGate page="lawfirm"><LawfirmMarketing /></RedirectGate>} />
          <Route path="/lawfirm/login" element={<Login key="lf-login" portal="lawfirm" />} />
          <Route path="/lawfirm/register" element={<Login key="lf-register" portal="lawfirm" initialMode="signup" />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/:slug/portal" element={<SlugRedirect><ClientLogin /></SlugRedirect>} />
          <Route path="/:slug/admin" element={<SlugRedirect><FirmLogin /></SlugRedirect>} />
          <Route path="/consult/:room" element={<ClientCall />} />
          <Route path="/estate" element={<ConsumerProtected><EstateHub /></ConsumerProtected>} />
          <Route path="/estate/mine" element={<ConsumerProtected><MyEstate /></ConsumerProtected>} />
          <Route path="/estate/join/:room" element={<ConsumerProtected><EstateJoin /></ConsumerProtected>} />
          <Route path="/estate/session/:docId" element={<ConsumerProtected><EstateRoom /></ConsumerProtected>} />
          <Route path="/estate/thread/:docId" element={<ConsumerProtected><EstateThread /></ConsumerProtected>} />
          <Route path="/app/estate/thread/:docId" element={<ConsumerProtected><EstateThread /></ConsumerProtected>} />
          <Route path="/app/estate/mine" element={<ConsumerProtected><MyEstate /></ConsumerProtected>} />
          <Route path="/app/estate/join/:room" element={<ConsumerProtected><EstateJoin /></ConsumerProtected>} />
          <Route path="/app/estate/session/:docId" element={<ConsumerProtected><EstateRoom /></ConsumerProtected>} />
          <Route path="/app/estate/:doc" element={<ConsumerProtected><EstateDraft /></ConsumerProtected>} />
          <Route path="/estate/verify-identity" element={<ConsumerProtected><ConsumerEstateVerify /></ConsumerProtected>} />
          <Route path="/app/estate/verify-identity" element={<ConsumerProtected><ConsumerEstateVerify /></ConsumerProtected>} />
          <Route path="/estate/:doc" element={<ConsumerProtected><EstateDraft /></ConsumerProtected>} />
          <Route path="/:slug/book/:attorneyId" element={<SlugRedirect><ConsultBooking /></SlugRedirect>} />
          <Route path="/:slug" element={<SlugRedirect><PublicFirmSite /></SlugRedirect>} />
          <Route path="/forms/:token" element={<PublicForm />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/link-support" element={<LinkSupport />} />
          <Route path="/client" element={<Protected client><ClientPortal /></Protected>} />
          <Route path="/payment/success" element={<PaymentResult />} />
          <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
          <Route path="/focused" element={<Protected><Focused /></Protected>} />
          <Route path="/intake/:leadId/call" element={<Protected><CallRoom /></Protected>} />
          <Route path="/platform" element={<PlatformProtected><PlatformLayout /></PlatformProtected>}>
            <Route index element={<Platform />} />
            <Route path="profile" element={<PlatformProfile />} />
            <Route path="firm-users/:uid" element={<FirmUserDetail />} />
            <Route path="tenants/:tid" element={<TenantDetail />} />
            <Route path="consumers/:id" element={<ConsumerDetail />} />
            <Route path="org-users/:id" element={<ConsumerDetail org />} />
            <Route path="pros/:id" element={<ProDetail />} />
            <Route path="identity/:kind/:sid" element={<PlatformIdentityDetail />} />
          </Route>
          <Route path="/" element={<RootProtected><Layout /></RootProtected>}>
            <Route index element={<Dashboard scope="mine" />} />
            <Route path="firm-matters" element={<Dashboard scope="firm" />} />
            <Route path="messages" element={<div className="h-full w-full px-4 sm:px-6 lg:px-8 py-7 fade-up flex flex-col"><FirmMessages /></div>} />
            <Route path="scheduling" element={<div className="w-full px-4 sm:px-6 lg:px-8 py-7 fade-up"><FirmScheduling /></div>} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="firm" element={<Firm />} />
            <Route path="intake" element={<div className="w-full px-4 sm:px-6 lg:px-8 py-7 fade-up"><FirmIntake /></div>} />
            <Route path="intake/:leadId" element={<div className="w-full px-4 sm:px-6 lg:px-8 py-7 fade-up"><IntakeDetail /></div>} />
            <Route path="conflicts" element={<Conflicts />} />
            <Route path="conflicts/:entryId" element={<ConflictDetail />} />
            <Route path="ai-training" element={<AdminOnly><FirmAITraining /></AdminOnly>} />
            <Route path="emails" element={<Emails />} />
            <Route path="identities" element={<AdminOnly><FirmIdentities /></AdminOnly>} />
            <Route path="payments" element={<AdminOnly><FirmPayments /></AdminOnly>} />
            <Route path="identities/:leadId" element={<AdminOnly><FirmIdentityDetail /></AdminOnly>} />
            <Route path="phone-api" element={<AdminOnly><FirmPhoneApi /></AdminOnly>} />
            <Route path="phone-calls" element={<FirmPhoneCalls />} />
            <Route path="phone-dialer" element={<PhoneDialer />} />
            <Route path="document-types" element={<DocumentTypes />} />
            <Route path="ai-research" element={<AIResearch />} />
            <Route path="firm/forms/new" element={<FormBuilderPage />} />
            <Route path="firm/forms/:formId" element={<FormBuilderPage />} />
            <Route path="firm/agreements/new" element={<AgreementBuilderPage />} />
            <Route path="firm/agreements/:templateId" element={<AgreementBuilderPage />} />
            <Route path="matters/:matterId" element={<MatterHome />} />
            <Route path="matters/:matterId/appointments" element={<MatterAppointments />} />
            <Route path="matters/:matterId/package" element={<PackageServices />} />
            <Route path="matters/:matterId/claims" element={<ClaimsMap />} />
            <Route path="matters/:matterId/claims/:claimId" element={<ClaimDetail />} />
            <Route path="matters/:matterId/documents" element={<Documents />} />
            <Route path="matters/:matterId/summarizer" element={<Summarizer />} />
            <Route path="matters/:matterId/emails" element={<MatterEmails />} />
            <Route path="matters/:matterId/process" element={<ProcessManager />} />
            <Route path="matters/:matterId/evidence" element={<FirmEvidenceReview />} />
            <Route path="matters/:matterId/research" element={<Research />} />
            <Route path="matters/:matterId/activity" element={<Activity />} />
            <Route path="matters/:matterId/assistant" element={<Assistant />} />
          </Route>
        </Routes>
        </Suspense>
        </FirmPhone>
        </UploadProvider>
      </BrowserRouter>
      </ConsumerBillingProvider>
      </ConsumerAuthProvider>
    </AuthProvider>
  );
}

export default App;
